import axios from "axios";
import { getApiBaseUrl } from "./apiConfig";
import { shouldBypassSlackConnection } from "./config";

// Simple cache with size limitation and expiration
const simpleCache = {
  // Cache raw API data with a short expiration
  _apiData: new Map(),
  
  // Maximum entries to store
  _maxEntries: 10,
  
  // Default expiration time (5 minutes)
  _defaultExpiration: 5 * 60 * 1000,
  
  // Get a cached value
  get(key) {
    const item = this._apiData.get(key);
    if (!item) return null;
    
    // Check if expired
    if (Date.now() > item.expiration) {
      this._apiData.delete(key);
      return null;
    }
    
    return item.value;
  },
  
  // Set a cached value
  set(key, value, customExpiration) {
    // If cache is too big, remove oldest entries 
    if (this._apiData.size >= this._maxEntries) {
      const oldestKey = Array.from(this._apiData.keys())[0];
      this._apiData.delete(oldestKey);
    }
    
    const expiration = Date.now() + (customExpiration || this._defaultExpiration);
    this._apiData.set(key, { value, expiration });
  },
  
  // Clear cache
  clear() {
    this._apiData.clear();
  }
};

// Fetch organization details
export const fetchOrganization = async (organizationId) => {
  try {
    const token = localStorage.getItem("token");
    const apiUrl = getApiBaseUrl();
    const response = await axios.get(
      `${apiUrl}/organization/${organizationId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching organization data:", error);
    throw error;
  }
};

// Check if Slack is connected
export const checkSlackConnection = async (organization) => {
  // Bypass Slack connection check when using mock data in development
  if (shouldBypassSlackConnection()) {
    console.log('Using mock data - bypassing Slack connection check');
    return true;
  }

  if (!organization) {
    throw new Error("Cannot check Slack connection: organization not loaded");
  }

  try {
    const token = localStorage.getItem("token");
    const apiUrl = getApiBaseUrl();
    const orgId = organization._id || organization.id;
    
    if (!orgId) {
      throw new Error("Organization ID is undefined");
    }
    
    const response = await axios.get(
      `${apiUrl}/integrations/status/${orgId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    
    return response.data.slack.connected;
  } catch (error) {
    console.error("Error checking Slack connection:", error);
    throw error;
  }
};

// Fetch Slack users
export const fetchSlackUsers = async (organization) => {
  // REMOVED: Mock data logic was here - now always uses real Slack API data
  // Mock data has been disabled across the application per configuration

  if (!organization) return [];
  
  const orgId = organization._id || organization.id;
  const cacheKey = `users_${orgId}`;
  
  // Check cache first
  const cachedUsers = simpleCache.get(cacheKey);
  if (cachedUsers) {
    console.log('Using cached users data');
    return cachedUsers;
  }
  
  try {
    const token = localStorage.getItem("token");
    const apiUrl = getApiBaseUrl();
    
    const response = await axios.get(
      `${apiUrl}/integrations/slack/users/${orgId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    
    const users = Array.isArray(response.data) ? response.data : [];
    
    // Cache the response
    simpleCache.set(cacheKey, users);
    
    return users;
  } catch (error) {
    console.error("Error fetching Slack users:", error);
    return [];
  }
};

// Fetch Slack channels
export const fetchSlackChannels = async (organization) => {
  // REMOVED: Mock data logic was here - now always uses real Slack API data
  // Mock data has been disabled across the application per configuration

  if (!organization) return [];
  
  const orgId = organization._id || organization.id;
  const cacheKey = `channels_${orgId}`;
  
  // Check cache first
  const cachedChannels = simpleCache.get(cacheKey);
  if (cachedChannels) {
    console.log('Using cached channels data');
    return cachedChannels;
  }
  
  try {
    const token = localStorage.getItem("token");
    const apiUrl = getApiBaseUrl();
    
    const response = await axios.get(
      `${apiUrl}/integrations/slack/channels/${orgId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    
    const channels = Array.isArray(response.data) ? response.data : [];
    
    // Cache the response
    simpleCache.set(cacheKey, channels);
    
    return channels;
  } catch (error) {
    console.error("Error fetching Slack channels:", error);
    return [];
  }
};

// Helper function to parse a timestamp
const parseTimestamp = (ts) => {
  if (!ts) return null;
  
  try {
    const timestamp = parseFloat(ts);
    if (isNaN(timestamp)) return null;
    return new Date(timestamp * 1000);
  } catch (error) {
    return null;
  }
};

// Check if a date falls within a range
const isInDateRange = (date, startDate, endDate) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return false;
  if (!startDate || !(startDate instanceof Date) || isNaN(startDate.getTime())) return false;
  if (!endDate || !(endDate instanceof Date) || isNaN(endDate.getTime())) return false;
  
  return date >= startDate && date <= endDate;
};

// Fetch channel messages
export const fetchChannelMessages = async (organization, channelId, limit = 100) => {
  // REMOVED: Mock data logic was here - now always uses real Slack API data
  // Mock data has been disabled across the application per configuration


  
  if (!organization) return [];
  
  const orgId = organization._id || organization.id;
  const cacheKey = `messages_${orgId}_${channelId}`;
  
  // Check cache first
  const cachedMessages = simpleCache.get(cacheKey);
  if (cachedMessages) {
    console.log(`Using cached messages for channel ${channelId}`);
    return cachedMessages;
  }

  try {
    // In a real implementation, this would make an API call to Slack
    // For now, return empty array for production
    console.log(`Fetching messages for channel ${channelId} from Slack API`);
    
    // Placeholder for real Slack API call
    const messages = [];
    
    // Cache the result
    simpleCache.set(cacheKey, messages);
    
    return messages;
  } catch (error) {
    console.error(`Error fetching messages for channel ${channelId}:`, error);
    return [];
  }
};

// Calculate engagement score (0-100) based on message frequency, reactions, and thread replies
export const calculateEngagementScore = (messages, users) => {
  if (!messages || messages.length === 0) return 0;
  
  // Count message frequency per user
  const userMessageCounts = {};
  const userReactionCounts = {};
  const userThreadReplies = {};
  let totalReactions = 0;
  let totalThreads = 0;
  
  messages.forEach(message => {
    // Count messages
    if (message.user) {
      userMessageCounts[message.user] = (userMessageCounts[message.user] || 0) + 1;
    }
    
    // Count reactions received
    if (message.reactions && Array.isArray(message.reactions)) {
      const reactionCount = message.reactions.reduce((sum, reaction) => sum + reaction.count, 0);
      totalReactions += reactionCount;
      
      if (message.user) {
        userReactionCounts[message.user] = (userReactionCounts[message.user] || 0) + reactionCount;
      }
    }
    
    // Count thread replies
    if (message.thread_ts && message.reply_count) {
      totalThreads += message.reply_count;
      
      if (message.user) {
        userThreadReplies[message.user] = (userThreadReplies[message.user] || 0) + message.reply_count;
      }
    }
  });
  
  // Calculate participation rate (percentage of users who have sent messages)
  const activeUsers = Object.keys(userMessageCounts).length;
  const participationRate = users.length > 0 ? (activeUsers / users.length) * 100 : 0;
  
  // Calculate average messages per user
  const avgMessagesPerUser = activeUsers > 0 ? 
    Object.values(userMessageCounts).reduce((sum, count) => sum + count, 0) / activeUsers : 0;
  
  // Calculate reaction rate (average reactions per message)
  const reactionRate = messages.length > 0 ? (totalReactions / messages.length) * 10 : 0;
  
  // Calculate thread engagement rate
  const threadRate = messages.length > 0 ? (totalThreads / messages.length) * 20 : 0;
  
  // Calculate final score (weighted combination of factors)
  const participationScore = Math.min(participationRate, 100);
  const messageScore = Math.min(avgMessagesPerUser * 5, 100);  // Scale messages per user
  const reactionScore = Math.min(reactionRate, 100);           // Scale reactions 
  const threadScore = Math.min(threadRate, 100);               // Scale thread engagement
  
  // Weight the different components (adjust weights as needed)
  const weightedScore = (
    participationScore * 0.3 + 
    messageScore * 0.3 + 
    reactionScore * 0.2 + 
    threadScore * 0.2
  );
  
  return Math.round(Math.min(weightedScore, 100));
};

// Generate engagement data for time series based on date range
export const generateEngagementTimeData = async (organization, channelIds, dateRange) => {
  if (!organization) return [];
  
  const orgId = organization._id || organization.id;
  const cacheKey = `engagement_time_${orgId}_${dateRange.range}_${channelIds ? channelIds.join('_') : 'all'}`;
  
  // Check cache first
  const cachedData = simpleCache.get(cacheKey);
  if (cachedData) {
    console.log('Using cached engagement time data');
    return cachedData;
  }
  
  try {
    const token = localStorage.getItem("token");
    const apiUrl = getApiBaseUrl();
    
    console.log(`Fetching engagement time data from: ${apiUrl}/slack-reports/${orgId}`);
    
    // Prepare query parameters
    const params = {
      startDate: dateRange.startDate.toISOString(),
      endDate: dateRange.endDate.toISOString(),
      rangeType: dateRange.range
    };
    
    if (channelIds && channelIds.length > 0) {
      params.channels = channelIds.join(',');
    }
    
    // Fetch detailed report data
    const response = await axios.get(
      `${apiUrl}/slack-reports/${orgId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params
      }
    );
    
    // Check if we have valid data
    if (!response.data?.data?.reportData?.timeSeriesData) {
      console.warn('No time series data available in the report');
      return [];
    }
    
    // Extract time series data from the report
    const timeSeriesData = response.data.data.reportData.timeSeriesData;
    
    // Transform data for the chart (multiply by 100 to get percentage)
    const formattedData = timeSeriesData.map(item => ({
      date: item.date,
      score: Math.round(item.score * 100), // Convert from 0-1 to 0-100 scale
      messageCount: item.messageCount || 0
    }));
    
    // Sort by date
    formattedData.sort((a, b) => new Date(a.date) - new Date(b.date));
    
    // Cache the result
    simpleCache.set(cacheKey, formattedData);
    
    return formattedData;
  } catch (error) {
    console.error("Error generating engagement time data:", error);
    return [];
  }
};

// Generate activity heatmap data (day of week vs hour of day)
export const generateActivityHeatmapData = async (organization, channelIds, dateRange) => {
  if (!organization) return [];
  
  const orgId = organization._id || organization.id;
  const cacheKey = `activity_heatmap_${orgId}_${dateRange.range}_${channelIds ? channelIds.join('_') : 'all'}`;
  
  // Check cache first
  const cachedData = simpleCache.get(cacheKey);
  if (cachedData) {
    console.log('Using cached activity heatmap data');
    return cachedData;
  }
  
  try {
    const token = localStorage.getItem("token");
    const apiUrl = getApiBaseUrl();
    
    console.log(`Fetching activity heatmap data from: ${apiUrl}/slack-reports/${orgId}`);
    
    // Prepare query parameters
    const params = {
      startDate: dateRange.startDate.toISOString(),
      endDate: dateRange.endDate.toISOString(),
      rangeType: dateRange.range
    };
    
    if (channelIds && channelIds.length > 0) {
      params.channels = channelIds.join(',');
    }
    
    // Fetch detailed report data
    const response = await axios.get(
      `${apiUrl}/slack-reports/${orgId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params
      }
    );
    
    // Check if we have valid data
    if (!response.data?.data?.reportData?.fullAnalysis?.engagementAnalysis?.hourlyHeatmap) {
      console.warn('No hourly heatmap data available in the report');
      return [];
    }
    
    // Extract hourly heatmap data from the report
    const hourlyHeatmap = response.data.data.reportData.fullAnalysis.engagementAnalysis.hourlyHeatmap;
    
    // Map activity levels to numeric values
    const activityLevelMap = {
      'very_low': 10,
      'low': 30,
      'medium': 50,
      'high': 70,
      'very_high': 85,
      'extreme': 100
    };
    
    // Transform the data for the heatmap component
    const formattedData = [];
    
    // Process each hour
    Object.keys(hourlyHeatmap).forEach(hour => {
      const hourData = hourlyHeatmap[hour];
      
      // Process each day of the week for this hour
      Object.keys(hourData).forEach(day => {
        const activityLevel = hourData[day];
        const numericValue = activityLevelMap[activityLevel] || 0;
        
        formattedData.push({
          hour: `${hour}:00`,
          hourIndex: parseInt(hour),
          day: day,
          value: numericValue,
          level: activityLevel
        });
      });
    });
    
    // Cache the result
    simpleCache.set(cacheKey, formattedData);
    
    return formattedData;
  } catch (error) {
    console.error("Error generating activity heatmap data:", error);
    return [];
  }
};

// Generate channel engagement metrics
export const generateChannelEngagementData = async (organization, dateRange) => {
  if (!organization) return [];
  
  const orgId = organization._id || organization.id;
  const cacheKey = `channel_engagement_${orgId}_${dateRange.range}`;
  
  // Check cache first
  const cachedData = simpleCache.get(cacheKey);
  if (cachedData) {
    console.log('Using cached channel engagement data');
    return cachedData;
  }
  
  try {
    const token = localStorage.getItem("token");
    const apiUrl = getApiBaseUrl();
    
    console.log(`Fetching channel engagement data from: ${apiUrl}/slack-reports/${orgId}`);
    
    // Prepare query parameters
    const params = {
      startDate: dateRange.startDate.toISOString(),
      endDate: dateRange.endDate.toISOString(),
      rangeType: dateRange.range
    };
    
    // Fetch detailed report data
    const response = await axios.get(
      `${apiUrl}/slack-reports/${orgId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params
      }
    );
    
    // Check if we have valid channel data
    if (!response.data?.data?.reportData?.channelData) {
      console.warn('No channel data available in the report');
      return [];
    }
    
    // Extract channel data from the report
    const channelData = response.data.data.reportData.channelData;
    
    // Get peak activity times from the weekly patterns
    const weeklyPatterns = response.data.data.reportData.fullAnalysis?.engagementAnalysis?.weeklyPatterns || {};
    
    // Transform the data for the channel engagement table
    const formattedData = channelData.map(channel => {
      // Format activity level - capitalize first letter
      const activityLevel = channel.activity.charAt(0).toUpperCase() + channel.activity.slice(1).replace('_', ' ');
      
      // Find peak activity for this channel from channel_engagement data
      const channelEngagement = response.data.data.reportData.fullAnalysis?.engagementAnalysis?.channelEngagement || [];
      const channelDetail = channelEngagement.find(c => c.channel_id === channel.id);
      
      return {
        id: channel.id,
        channel: channel.channel,
        score: Math.round(channel.score * 100), // Convert from 0-1 to 0-100 scale
        activity: activityLevel,
        peakActivity: channelDetail?.peak_activity || 'N/A',
        messages: channel.messages || 0,
        reactions: channelDetail?.reaction_count || 0,
        contributors: channelDetail?.active_contributors || 0
      };
    });
    
    // Sort by score (descending)
    formattedData.sort((a, b) => b.score - a.score);
    
    // Cache the result
    simpleCache.set(cacheKey, formattedData);
    
    return formattedData;
  } catch (error) {
    console.error("Error generating channel engagement data:", error);
    return [];
  }
};

// Check if a date is within range for filtering messages
const isDateInRange = (messageTimestamp, startDate, endDate) => {
  if (!messageTimestamp) return false;
  
  try {
    const messageDate = new Date(parseFloat(messageTimestamp) * 1000);
    
    // Check if the date is valid
    if (isNaN(messageDate.getTime())) {
      return false;
    }
    
    // Ensure start and end date are Date objects
    const start = startDate instanceof Date ? startDate : new Date(startDate);
    const end = endDate instanceof Date ? endDate : new Date(endDate);
    
    return messageDate >= start && messageDate <= end;
  } catch (error) {
    console.error("Error checking date range:", error);
    return false;
  }
};

// Clear all caches (for testing or when needed)
export const clearCache = () => {
  simpleCache.clear();
}; 

// Generate sentiment time series data
export const generateSentimentTimeData = async (organization, channelIds, dateRange) => {
  if (!organization) return [];
  
  // REMOVED: Mock data logic was here - now always uses real Slack API data
  // Mock data has been disabled across the application per configuration
  
  try {
    const token = localStorage.getItem("token");
    const apiUrl = getApiBaseUrl();
    const orgId = organization._id || organization.id;
    
    const startDate = new Date(dateRange.startDate).toISOString();
    const endDate = new Date(dateRange.endDate).toISOString();
    
    // Create cache key with relevant filter parameters
    const cacheKey = `sentiment_time_${orgId}_${startDate}_${endDate}_${channelIds ? channelIds.join('-') : 'all'}`;
    
    // Check cache first
    const cachedData = simpleCache.get(cacheKey);
    if (cachedData) {
      console.log('Using cached sentiment time data');
      return cachedData;
    }
    
    // Use the new report endpoint instead of old analytics endpoint
    const response = await axios.get(
      `${apiUrl}/slack-reports/organization/${orgId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          startDate,
          endDate,
          // Removed date filtering to get all data from database
          // channels: channelIds ? channelIds.join(',') : undefined
        }
      }
    );
    
    if (!response.data.success || !response.data.data?.reportData?.sentiment) {
      console.log('No sentiment data available in report');
      return [];
    }
    
    const sentimentData = response.data.data.reportData.sentiment;
    console.log('Sentiment data from DB:', sentimentData);
    console.log('Sentiment score keys:', Object.keys(sentimentData.sentimentScore || {}));
    
    // Filter data by date range first, then convert
    const filterStartDate = new Date(dateRange.startDate);
    const filterEndDate = new Date(dateRange.endDate);
    
    // Convert the new structure to the expected format
    const timeData = [];
    if (sentimentData.sentimentScore) {
      Object.entries(sentimentData.sentimentScore).forEach(([dateStr, score]) => {
        // Parse the date string format "Jun 23 2025, Monday"
        const datePart = dateStr.split(',')[0].trim();
        // Parse date parts manually to avoid timezone-driven date shifts from toISOString()
        const [mName, mDay, mYear] = datePart.split(' ');
        const MONTH_NUMS = { Jan:0, Feb:1, Mar:2, Apr:3, May:4, Jun:5, Jul:6, Aug:7, Sep:8, Oct:9, Nov:10, Dec:11 };
        const monthIdx = MONTH_NUMS[mName];
        const dayNum = parseInt(mDay);
        const yearNum = parseInt(mYear);
        const dateObj = new Date(yearNum, monthIdx, dayNum); // local midnight, safe for comparison
        
        // Check if this date is in the filter range
        if (dateObj >= filterStartDate && dateObj <= filterEndDate) {
          const isoDate = `${yearNum}-${String(monthIdx + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
          
          // Convert score from 0-1 to -1 to 1 range for sentiment
          const normalizedScore = (score - 0.5) * 2;
          
          timeData.push({
            date: isoDate,
            score: parseFloat(normalizedScore.toFixed(2)),
            positive: Math.round(score * 100), // Approximate positive percentage
            neutral: Math.round((1 - Math.abs(normalizedScore)) * 50), // Approximate neutral
            negative: Math.round((1 - score) * 100), // Approximate negative percentage
            totalMessages: 0 // Would need to be calculated from raw data
          });
        }
      });
    }
    
    // Sort by date
    timeData.sort((a, b) => new Date(a.date) - new Date(b.date));
    
    console.log(`Filtered sentiment data from ${Object.keys(sentimentData.sentimentScore || {}).length} to ${timeData.length} items`);
    
    // Cache the response
    simpleCache.set(cacheKey, timeData);
    
    return timeData;
  } catch (error) {
    console.error("Error generating sentiment time data:", error);
    return [];
  }
};

// Generate sentiment distribution data
export const generateSentimentDistributionData = async (organization, channelIds, dateRange) => {
  if (!organization) return [];
  
  // REMOVED: Mock data logic was here - now always uses real Slack API data
  // Mock data has been disabled across the application per configuration
  
  try {
    const token = localStorage.getItem("token");
    const apiUrl = getApiBaseUrl();
    const orgId = organization._id || organization.id;
    
    const startDate = new Date(dateRange.startDate).toISOString();
    const endDate = new Date(dateRange.endDate).toISOString();
    
    // Create cache key with relevant filter parameters
    const cacheKey = `sentiment_dist_${orgId}_${startDate}_${endDate}_${channelIds ? channelIds.join('-') : 'all'}`;
    
    // Check cache first
    const cachedData = simpleCache.get(cacheKey);
    if (cachedData) {
      console.log('Using cached sentiment distribution data');
      return cachedData;
    }
    
    // Use the new report endpoint instead of old analytics endpoint
    const response = await axios.get(
      `${apiUrl}/slack-reports/organization/${orgId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          // Removed date filtering to get all data from database
        }
      }
    );
    
    if (!response.data.success || !response.data.data?.reportData?.sentiment) {
      console.log('No sentiment data available in report');
      return [
        { name: "Positive", value: 0, color: "#14B882" },
        { name: "Neutral", value: 0, color: "#ABABBA" },
        { name: "Negative", value: 0, color: "#E50026" }
      ];
    }
    
    const sentimentData = response.data.data.reportData.sentiment;
    
    // Filter data by date range first
    const filterStartDate = new Date(dateRange.startDate);
    const filterEndDate = new Date(dateRange.endDate);
    
    console.log('Processing sentiment distribution for date range:', { filterStartDate, filterEndDate });
    console.log('Available sentiment data keys:', {
      distributionKeys: sentimentData.sentimentDistribution ? Object.keys(sentimentData.sentimentDistribution) : [],
      scoreKeys: sentimentData.sentimentScore ? Object.keys(sentimentData.sentimentScore) : []
    });
    
    // Calculate distribution from sentiment distribution data or sentiment scores
    let distributionData = [
      { name: "Positive", value: 0, color: "#14B882" },
      { name: "Neutral", value: 0, color: "#ABABBA" },
      { name: "Negative", value: 0, color: "#E50026" }
    ];
    
    if (sentimentData.sentimentDistribution && Object.keys(sentimentData.sentimentDistribution).length > 0) {
      // Filter distribution data by date range and aggregate
      let totalPositive = 0, totalNeutral = 0, totalNegative = 0;
      let daysCount = 0;
      
      Object.entries(sentimentData.sentimentDistribution).forEach(([dateStr, distribution]) => {
        // Parse the date string format "Jun 23 2025, Monday"
        const datePart = dateStr.split(',')[0].trim();
        const messageDate = new Date(datePart);
        if (messageDate >= filterStartDate && messageDate <= filterEndDate) {
          totalPositive += distribution.positive || 0;
          totalNeutral += distribution.neutral || 0;
          totalNegative += distribution.negative || 0;
          daysCount++;
        }
      });
      
      if (daysCount > 0) {
        // Calculate average distribution across the date range
        const avgPositive = totalPositive / daysCount;
        const avgNeutral = totalNeutral / daysCount;
        const avgNegative = totalNegative / daysCount;
        
        distributionData = [
          { name: "Positive", value: Math.round(avgPositive * 100), color: "#14B882" },
          { name: "Neutral", value: Math.round(avgNeutral * 100), color: "#ABABBA" },
          { name: "Negative", value: Math.round(avgNegative * 100), color: "#E50026" }
        ];
        
        console.log('Calculated sentiment distribution from daily data:', {
          daysCount,
          avgPositive,
          avgNeutral,
          avgNegative,
          distributionData
        });
      }
    } else if (sentimentData.sentimentScore && Object.keys(sentimentData.sentimentScore).length > 0) {
      // Calculate from sentiment scores in date range
      const filteredScores = [];
      
      Object.entries(sentimentData.sentimentScore).forEach(([dateStr, score]) => {
        // Parse the date string format "Jun 23 2025, Monday"
        const datePart = dateStr.split(',')[0].trim();
        const messageDate = new Date(datePart);
        if (messageDate >= filterStartDate && messageDate <= filterEndDate) {
          filteredScores.push(score);
        }
      });
      
      if (filteredScores.length > 0) {
        const avgScore = filteredScores.reduce((sum, score) => sum + score, 0) / filteredScores.length;
        
        // Convert 0-1 score to distribution based on score ranges
        let positivePercent, neutralPercent, negativePercent;
        
        if (avgScore >= 0.6) {
          // High sentiment score -> mostly positive
          positivePercent = 60 + (avgScore - 0.6) * 100; // 60-100%
          neutralPercent = Math.max(10, 40 - (avgScore - 0.6) * 100); // 10-40%
          negativePercent = Math.max(0, 100 - positivePercent - neutralPercent);
        } else if (avgScore >= 0.4) {
          // Moderate sentiment score -> mostly neutral
          neutralPercent = 50 + (0.6 - avgScore) * 50; // 40-60%
          positivePercent = avgScore * 50; // 20-30%
          negativePercent = Math.max(0, 100 - positivePercent - neutralPercent);
        } else {
          // Low sentiment score -> more negative
          negativePercent = 20 + (0.4 - avgScore) * 75; // 20-50%
          neutralPercent = Math.max(20, 60 - negativePercent); // 20-40%
          positivePercent = Math.max(0, 100 - negativePercent - neutralPercent);
        }
        
        distributionData = [
          { name: "Positive", value: Math.round(positivePercent), color: "#14B882" },
          { name: "Neutral", value: Math.round(neutralPercent), color: "#ABABBA" },
          { name: "Negative", value: Math.round(negativePercent), color: "#E50026" }
        ];
        
        console.log('Calculated sentiment distribution from scores:', {
          scoresCount: filteredScores.length,
          avgScore,
          distributionData
        });
      }
    }
    
    console.log('Final sentiment distribution data:', distributionData);
    
    // Cache the response
    simpleCache.set(cacheKey, distributionData);
    
    return distributionData;
  } catch (error) {
    console.error("Error generating sentiment distribution data:", error);
    // Return fallback data structure for UI
    return [
      { name: "Positive", value: 0, color: "#14B882" },
      { name: "Neutral", value: 0, color: "#ABABBA" },
      { name: "Negative", value: 0, color: "#E50026" }
    ];
  }
};

// Generate channel sentiment data
export const generateChannelSentimentData = async (organization, dateRange) => {
  if (!organization) return [];
  
  // REMOVED: Mock data logic was here - now always uses real Slack API data
  // Mock data has been disabled across the application per configuration
  
  try {
    const token = localStorage.getItem("token");
    const apiUrl = getApiBaseUrl();
    const orgId = organization._id || organization.id;
    
    const startDate = new Date(dateRange.startDate).toISOString();
    const endDate = new Date(dateRange.endDate).toISOString();
    
    // Create cache key with relevant filter parameters
    const cacheKey = `sentiment_channels_${orgId}_${startDate}_${endDate}`;
    
    // Check cache first
    const cachedData = simpleCache.get(cacheKey);
    if (cachedData) {
      console.log('Using cached channel sentiment data');
      return cachedData;
    }
    
    // Use the new report endpoint instead of old analytics endpoint
    const response = await axios.get(
      `${apiUrl}/slack-reports/organization/${orgId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          startDate,
          endDate
        }
      }
    );
    
    if (!response.data.success || !response.data.data?.reportData?.sentiment) {
      console.log('No sentiment data available in report');
      return [];
    }
    
    const sentimentData = response.data.data.reportData.sentiment;
    
    // Filter data by date range first
    const filterStartDate = new Date(dateRange.startDate);
    const filterEndDate = new Date(dateRange.endDate);
    
    // Convert channel sentiment data from new structure
    const channelData = [];
    if (sentimentData.channelSentiment) {
      Object.entries(sentimentData.channelSentiment).forEach(([channelName, channelDateData]) => {
        // Filter channel data by date range and aggregate
        const filteredScores = [];
        const aggregatedWords = new Map();
        
        Object.entries(channelDateData).forEach(([dateStr, dayData]) => {
          // Parse the date string format "Jun 23 2025, Monday"
          const datePart = dateStr.split(',')[0].trim();
          const messageDate = new Date(datePart);
          
          if (messageDate >= filterStartDate && messageDate <= filterEndDate) {
            if (dayData.sentimentScore !== undefined) {
              filteredScores.push(dayData.sentimentScore);
            }
            
            // Aggregate frequent words for this channel
            if (dayData.frequentWords && Array.isArray(dayData.frequentWords)) {
              dayData.frequentWords.forEach(wordData => {
                const word = wordData.text;
                const count = wordData.count;
                const sentiment = wordData.sentiment;
                
                if (!aggregatedWords.has(word)) {
                  aggregatedWords.set(word, { count: 0, sentiment: sentiment });
                }
                aggregatedWords.get(word).count += count;
              });
            }
          }
        });
        
        if (filteredScores.length > 0) {
          // Calculate average sentiment for this channel in the date range
          const avgScore = filteredScores.reduce((sum, score) => sum + score, 0) / filteredScores.length;
          
          // Convert score to percentage and sentiment label
          const scorePercent = Math.round(avgScore * 100);
          let sentiment = 'neutral';
          if (avgScore > 0.6) sentiment = 'positive';
          else if (avgScore < 0.4) sentiment = 'negative';
          
          // Convert aggregated words to array and sort by frequency
          const frequentWords = Array.from(aggregatedWords.entries())
            .map(([word, data]) => ({
              text: word,
              count: data.count,
              sentiment: data.sentiment
            }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 5); // Top 5 words per channel
          
          channelData.push({
            id: channelName, // Add ID for the component
            channel: channelName,
            score: scorePercent,
            sentiment: sentiment,
            messages: filteredScores.length, // Use filtered data count
            positivePercent: Math.round(avgScore * 100),
            neutralPercent: Math.round((1 - Math.abs(avgScore - 0.5) * 2) * 100),
            negativePercent: Math.round((1 - avgScore) * 100),
            frequentWords: frequentWords // Add frequent words for the component
          });
        }
      });
    }
    
    // Get available channels to merge is_private property
    try {
      const availableChannels = await fetchSlackChannels(organization);
      const channelMap = new Map(availableChannels.map(ch => [ch.name, ch.is_private]));
      
      // Add is_private property to each channel data item
      const enrichedChannelData = channelData.map(item => ({
        ...item,
        is_private: channelMap.get(item.channel) || false
      }));
      
      // Cache the enriched response
      simpleCache.set(cacheKey, enrichedChannelData);
      
      return enrichedChannelData;
    } catch (error) {
      console.warn("Could not fetch channels for is_private property:", error);
      // Cache the response without is_private
      simpleCache.set(cacheKey, channelData);
      return channelData;
    }
  } catch (error) {
    console.error("Error generating channel sentiment data:", error);
    return [];
  }
};

// Generate top frequent words with sentiment
export const generateTopFrequentWords = async (organization, channelIds, dateRange) => {
  if (!organization) return [];
  
  // REMOVED: Mock data logic was here - now always uses real Slack API data
  // Mock data has been disabled across the application per configuration
  
  try {
    const token = localStorage.getItem("token");
    const apiUrl = getApiBaseUrl();
    const orgId = organization._id || organization.id;
    
    const startDate = new Date(dateRange.startDate).toISOString();
    const endDate = new Date(dateRange.endDate).toISOString();
    
    // Create cache key with relevant filter parameters
    const cacheKey = `frequent_words_${orgId}_${startDate}_${endDate}_${channelIds ? channelIds.join('-') : 'all'}`;
    
    // Check cache first
    const cachedData = simpleCache.get(cacheKey);
    if (cachedData) {
      console.log('Using cached frequent words data');
      return cachedData;
    }
    
    const response = await axios.get(
      `${apiUrl}/analytics/sentiment/words/${orgId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          startDate,
          endDate,
          channels: channelIds ? channelIds.join(',') : undefined,
          limit: 10
        }
      }
    );
    
    const wordsData = Array.isArray(response.data) ? response.data : [];
    
    // Cache the response
    simpleCache.set(cacheKey, wordsData);
    
    return wordsData;
  } catch (error) {
    console.error("Error generating frequent words data:", error);
    return [];
  }
}; 

// Generate tone time series data
export const generateToneTimeData = async (organization, channelIds, dateRange) => {
  if (!organization) return [];
  
  // REMOVED: Mock data logic was here - now always uses real Slack API data
  // Mock data has been disabled across the application per configuration
  
  try {
    const token = localStorage.getItem("token");
    const apiUrl = getApiBaseUrl();
    const orgId = organization._id || organization.id;
    
    const startDate = new Date(dateRange.startDate).toISOString();
    const endDate = new Date(dateRange.endDate).toISOString();
    
    // Create cache key with relevant filter parameters
    const cacheKey = `tone_time_${orgId}_${startDate}_${endDate}_${channelIds ? channelIds.join('-') : 'all'}`;
    
    // Check cache first
    const cachedData = simpleCache.get(cacheKey);
    if (cachedData) {
      console.log('Using cached tone time data');
      return cachedData;
    }
    
    const response = await axios.get(
      `${apiUrl}/analytics/tone/time/${orgId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          startDate,
          endDate,
          channels: channelIds ? channelIds.join(',') : undefined
        }
      }
    );
    
    const timeData = Array.isArray(response.data) ? response.data : [];
    
    // Cache the response
    simpleCache.set(cacheKey, timeData);
    
    return timeData;
  } catch (error) {
    console.error("Error generating tone time data:", error);
    return [];
  }
};

// Generate tone distribution data (radar chart)
export const generateToneDistributionData = async (organization, channelIds, dateRange) => {
  if (!organization) return [];
  
  // REMOVED: Mock data logic was here - now always uses real Slack API data
  // Mock data has been disabled across the application per configuration
  
  try {
    const token = localStorage.getItem("token");
    const apiUrl = getApiBaseUrl();
    const orgId = organization._id || organization.id;
    
    const startDate = new Date(dateRange.startDate).toISOString();
    const endDate = new Date(dateRange.endDate).toISOString();
    
    // Create cache key with relevant filter parameters
    const cacheKey = `tone_dist_${orgId}_${startDate}_${endDate}_${channelIds ? channelIds.join('-') : 'all'}`;
    
    // Check cache first
    const cachedData = simpleCache.get(cacheKey);
    if (cachedData) {
      console.log('Using cached tone distribution data');
      return cachedData;
    }
    
    const response = await axios.get(
      `${apiUrl}/analytics/tone/distribution/${orgId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          startDate,
          endDate,
          channels: channelIds ? channelIds.join(',') : undefined
        }
      }
    );
    
    const distributionData = Array.isArray(response.data) ? response.data : [];
    
    // Cache the response
    simpleCache.set(cacheKey, distributionData);
    
    return distributionData;
  } catch (error) {
    console.error("Error generating tone distribution data:", error);
    // Return fallback data structure for UI
    return [
      { tone: "Collaborative", value: 0, fullMark: 100 },
      { tone: "Formal", value: 0, fullMark: 100 },
      { tone: "Casual", value: 0, fullMark: 100 },
      { tone: "Urgent", value: 0, fullMark: 100 },
      { tone: "Assertive", value: 0, fullMark: 100 },
      { tone: "Supportive", value: 0, fullMark: 100 }
    ];
  }
};

// Generate channel tone data
export const generateChannelToneData = async (organization, dateRange) => {
  if (!organization) return [];
  
  // Return mock channel tone data when using mock data in development
  if (shouldBypassSlackConnection()) {
    console.log('Using mock data - generating channel tone data');
    
    try {
      // Get channels
      const channels = await fetchSlackChannels(organization);
      
      // Generate mock tone data for each channel
      const channelToneData = channels.map(channel => {
        // Generate varied tone characteristics for different channels
        let collaborativeBase, formalBase, casualBase, urgentBase, assertiveBase, supportiveBase;
        
        // Different tone profiles for different channels
        switch (channel.name) {
          case 'general':
            collaborativeBase = 75 + Math.random() * 15; // High collaboration
            formalBase = 30 + Math.random() * 20; // Less formal
            casualBase = 70 + Math.random() * 20; // More casual
            urgentBase = 15 + Math.random() * 15; // Low urgency
            assertiveBase = 40 + Math.random() * 20; // Moderate assertiveness
            supportiveBase = 80 + Math.random() * 15; // High support
            break;
          case 'development':
            collaborativeBase = 85 + Math.random() * 10; // Very collaborative
            formalBase = 60 + Math.random() * 20; // More formal (technical)
            casualBase = 40 + Math.random() * 20; // Less casual
            urgentBase = 30 + Math.random() * 25; // Higher urgency (bugs/deadlines)
            assertiveBase = 50 + Math.random() * 25; // More assertive
            supportiveBase = 70 + Math.random() * 20; // Supportive
            break;
          case 'design':
            collaborativeBase = 80 + Math.random() * 15; // High collaboration
            formalBase = 45 + Math.random() * 25; // Moderate formality
            casualBase = 65 + Math.random() * 20; // More casual (creative)
            urgentBase = 20 + Math.random() * 15; // Low urgency
            assertiveBase = 35 + Math.random() * 20; // Less assertive
            supportiveBase = 85 + Math.random() * 10; // Very supportive
            break;
          case 'marketing':
            collaborativeBase = 70 + Math.random() * 20; // Collaborative
            formalBase = 50 + Math.random() * 30; // Varied formality
            casualBase = 60 + Math.random() * 25; // Moderate casual
            urgentBase = 25 + Math.random() * 20; // Moderate urgency
            assertiveBase = 60 + Math.random() * 25; // More assertive
            supportiveBase = 75 + Math.random() * 15; // Supportive
            break;
          default:
            collaborativeBase = 65 + Math.random() * 25;
            formalBase = 45 + Math.random() * 30;
            casualBase = 55 + Math.random() * 25;
            urgentBase = 20 + Math.random() * 20;
            assertiveBase = 40 + Math.random() * 25;
            supportiveBase = 70 + Math.random() * 20;
        }
        
                 // Calculate overall tone score (weighted average, 0-1 scale)
         const toneScore = (
           (collaborativeBase * 0.25) +
           (supportiveBase * 0.25) +
           ((100 - urgentBase) * 0.2) + // Less urgent = better tone
           (formalBase * 0.15) +
           ((100 - assertiveBase) * 0.15) // Less assertive = better tone
         ) / 100;
         
         // Generate frequent tones for this channel
         const toneValues = {
           collaborative: collaborativeBase,
           formal: formalBase,
           casual: casualBase,
           urgent: urgentBase,
           assertive: assertiveBase,
           supportive: supportiveBase
         };
         
         // Sort tones by value and take top 3-4
         const sortedTones = Object.entries(toneValues)
           .sort(([,a], [,b]) => b - a)
           .slice(0, Math.floor(Math.random() * 2) + 3) // 3-4 tones
           .map(([tone, value]) => ({
             tone: tone,
             text: tone.charAt(0).toUpperCase() + tone.slice(1),
             value: Math.round(value)
           }));
         
         return {
           id: channel.id,
           channel: channel.name,
           is_private: channel.is_private, // Add is_private property for lock icon
           score: parseFloat(toneScore.toFixed(2)), // Overall tone score for sorting/display
           collaborative: Math.round(collaborativeBase),
           formal: Math.round(formalBase),
           casual: Math.round(casualBase),
           urgent: Math.round(urgentBase),
           assertive: Math.round(assertiveBase),
           supportive: Math.round(supportiveBase),
           totalMessages: Math.floor(Math.random() * 100) + 20, // 20-120 messages
           dominantTone: getDominantTone({
             collaborative: collaborativeBase,
             formal: formalBase,
             casual: casualBase,
             urgent: urgentBase,
             assertive: assertiveBase,
             supportive: supportiveBase
           }),
           frequentTones: sortedTones // This is what the table expects
         };
      });
      
      return channelToneData;
    } catch (error) {
      console.error("Error generating mock channel tone data:", error);
      return [];
    }
  }
  
  try {
    const token = localStorage.getItem("token");
    const apiUrl = getApiBaseUrl();
    const orgId = organization._id || organization.id;
    
    const startDate = new Date(dateRange.startDate).toISOString();
    const endDate = new Date(dateRange.endDate).toISOString();
    
    // Create cache key with relevant filter parameters
    const cacheKey = `tone_channels_${orgId}_${startDate}_${endDate}`;
    
    // Check cache first
    const cachedData = simpleCache.get(cacheKey);
    if (cachedData) {
      console.log('Using cached channel tone data');
      return cachedData;
    }
    
    const response = await axios.get(
      `${apiUrl}/analytics/tone/channels/${orgId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          startDate,
          endDate
        }
      }
    );
    
    const channelData = Array.isArray(response.data) ? response.data : [];
    
    // Get available channels to merge is_private property
    try {
      const availableChannels = await fetchSlackChannels(organization);
      const channelMap = new Map(availableChannels.map(ch => [ch.name, ch.is_private]));
      
      // Add is_private property to each channel data item
      const enrichedChannelData = channelData.map(item => ({
        ...item,
        is_private: channelMap.get(item.channel) || false
      }));
      
      // Cache the enriched response
      simpleCache.set(cacheKey, enrichedChannelData);
      
      return enrichedChannelData;
    } catch (error) {
      console.warn("Could not fetch channels for is_private property:", error);
      // Cache the response without is_private
      simpleCache.set(cacheKey, channelData);
      return channelData;
    }
  } catch (error) {
    console.error("Error generating channel tone data:", error);
    return [];
  }
};

// Helper function to determine dominant tone
const getDominantTone = (tones) => {
  const toneEntries = Object.entries(tones);
  const dominantEntry = toneEntries.reduce((max, current) => 
    current[1] > max[1] ? current : max
  );
  
  // Capitalize first letter
  return dominantEntry[0].charAt(0).toUpperCase() + dominantEntry[0].slice(1);
};

// Generate clarity time series data
export const generateClarityTimeData = async (organization, channelIds, dateRange) => {
  if (!organization) return [];
  
  // Return mock clarity data when using mock data in development
  if (shouldBypassSlackConnection()) {
    console.log('Using mock data - generating clarity time data');
    
    try {
      // Get date range
      let startDate = new Date(dateRange.startDate);
      let endDate = new Date(dateRange.endDate);
      
      // Set hours to ensure we capture the full days
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
      
      // Calculate number of days in the range
      const dateDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
      
      // Generate mock clarity data for each day
      const clarityData = [];
      for (let i = 0; i < dateDiff; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        const dateString = date.toISOString().split('T')[0];
        
        // Generate realistic clarity score (0 to 1 range, where higher = clearer communication)
        const baseClarity = 0.7 + Math.random() * 0.25; // 0.7-0.95 range for generally clear communication
        
        clarityData.push({
          date: dateString,
          score: Math.round(baseClarity * 100), // Convert to percentage (70-95%)
          readabilityScore: Math.round(70 + Math.random() * 25), // 70-95 readability
          coherenceScore: Math.round(65 + Math.random() * 30), // 65-95 coherence
          concisionScore: Math.round(60 + Math.random() * 35), // 60-95 concision
          jargonLevel: Math.round(10 + Math.random() * 20), // 10-30% jargon (lower is better)
          avgSentenceLength: Math.round(12 + Math.random() * 8), // 12-20 words per sentence
          totalMessages: Math.floor(Math.random() * 50) + 10 // 10-60 messages per day
        });
      }
      
      return clarityData;
    } catch (error) {
      console.error("Error generating mock clarity time data:", error);
      return [];
    }
  }
  
  try {
    const token = localStorage.getItem("token");
    const apiUrl = getApiBaseUrl();
    const orgId = organization._id || organization.id;
    
    const startDate = new Date(dateRange.startDate).toISOString();
    const endDate = new Date(dateRange.endDate).toISOString();
    
    // Create cache key with relevant filter parameters
    const cacheKey = `clarity_time_${orgId}_${startDate}_${endDate}_${channelIds ? channelIds.join('-') : 'all'}`;
    
    // Check cache first
    const cachedData = simpleCache.get(cacheKey);
    if (cachedData) {
      console.log('Using cached clarity time data');
      return cachedData;
    }
    
    const response = await axios.get(
      `${apiUrl}/analytics/clarity/time/${orgId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          startDate,
          endDate,
          channels: channelIds ? channelIds.join(',') : undefined
        }
      }
    );
    
    const timeData = Array.isArray(response.data) ? response.data : [];
    
    // Cache the response
    simpleCache.set(cacheKey, timeData);
    
    return timeData;
  } catch (error) {
    console.error("Error generating clarity time data:", error);
    return [];
  }
};

// Generate clarity distribution data
export const generateClarityDistributionData = async (organization, channelIds, dateRange) => {
  if (!organization) return [];
  
  // Return mock clarity distribution data when using mock data in development
  if (shouldBypassSlackConnection()) {
    console.log('Using mock data - generating clarity distribution data');
    
    // Generate realistic clarity distribution with some randomness
    const veryClear = Math.round(40 + Math.random() * 20); // 40-60%
    const clear = Math.round(25 + Math.random() * 15); // 25-40%
    const moderate = Math.round(15 + Math.random() * 15); // 15-30%
    const unclear = Math.round(5 + Math.random() * 10); // 5-15%
    const veryUnclear = Math.round(2 + Math.random() * 8); // 2-10%
    
    // Calculate message counts based on percentages (assuming 200 total messages)
    const totalMessages = 200;
    
    return [
      { 
        range: "80-100%", 
        count: Math.round((veryClear / 100) * totalMessages), 
        percentage: veryClear,
        color: "#14B882"
      },
      { 
        range: "60-79%", 
        count: Math.round((clear / 100) * totalMessages), 
        percentage: clear,
        color: "#10B981"
      },
      { 
        range: "40-59%", 
        count: Math.round((moderate / 100) * totalMessages), 
        percentage: moderate,
        color: "#F59E0B"
      },
      { 
        range: "20-39%", 
        count: Math.round((unclear / 100) * totalMessages), 
        percentage: unclear,
        color: "#EF4444"
      },
      { 
        range: "0-19%", 
        count: Math.round((veryUnclear / 100) * totalMessages), 
        percentage: veryUnclear,
        color: "#E50026"
      }
    ];
  }
  
  try {
    const token = localStorage.getItem("token");
    const apiUrl = getApiBaseUrl();
    const orgId = organization._id || organization.id;
    
    const startDate = new Date(dateRange.startDate).toISOString();
    const endDate = new Date(dateRange.endDate).toISOString();
    
    // Create cache key with relevant filter parameters
    const cacheKey = `clarity_dist_${orgId}_${startDate}_${endDate}_${channelIds ? channelIds.join('-') : 'all'}`;
    
    // Check cache first
    const cachedData = simpleCache.get(cacheKey);
    if (cachedData) {
      console.log('Using cached clarity distribution data');
      return cachedData;
    }
    
    const response = await axios.get(
      `${apiUrl}/analytics/clarity/distribution/${orgId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          startDate,
          endDate,
          channels: channelIds ? channelIds.join(',') : undefined
        }
      }
    );
    
    const distributionData = Array.isArray(response.data) ? response.data : [];
    
    // Cache the response
    simpleCache.set(cacheKey, distributionData);
    
    return distributionData;
  } catch (error) {
    console.error("Error generating clarity distribution data:", error);
    // Return fallback data structure for UI
    return [
      { range: "80-100%", count: 0, percentage: 0, color: "#14B882" },
      { range: "60-79%", count: 0, percentage: 0, color: "#10B981" },
      { range: "40-59%", count: 0, percentage: 0, color: "#F59E0B" },
      { range: "20-39%", count: 0, percentage: 0, color: "#EF4444" },
      { range: "0-19%", count: 0, percentage: 0, color: "#E50026" }
    ];
  }
};

// Generate channel clarity data
export const generateChannelClarityData = async (organization, dateRange) => {
  if (!organization) return [];
  
  // Return mock channel clarity data when using mock data in development
  if (shouldBypassSlackConnection()) {
    console.log('Using mock data - generating channel clarity data');
    
    try {
      // Get channels
      const channels = await fetchSlackChannels(organization);
      
      // Generate mock clarity data for each channel
      const channelClarityData = channels.map(channel => {
        // Generate varied clarity characteristics for different channels
        let readabilityBase, coherenceBase, concisionBase, jargonBase, sentenceLengthBase;
        
        // Different clarity profiles for different channels
        switch (channel.name) {
          case 'general':
            readabilityBase = 80 + Math.random() * 15; // High readability
            coherenceBase = 75 + Math.random() * 20; // Good coherence
            concisionBase = 70 + Math.random() * 20; // Moderately concise
            jargonBase = 15 + Math.random() * 10; // Low jargon
            sentenceLengthBase = 14 + Math.random() * 4; // 14-18 words
            break;
          case 'development':
            readabilityBase = 65 + Math.random() * 20; // Lower readability (technical)
            coherenceBase = 80 + Math.random() * 15; // High coherence (logical)
            concisionBase = 60 + Math.random() * 25; // Variable concision
            jargonBase = 30 + Math.random() * 20; // Higher jargon (technical terms)
            sentenceLengthBase = 16 + Math.random() * 6; // 16-22 words
            break;
          case 'design':
            readabilityBase = 85 + Math.random() * 10; // Very readable (visual focus)
            coherenceBase = 75 + Math.random() * 20; // Good coherence
            concisionBase = 80 + Math.random() * 15; // Quite concise
            jargonBase = 20 + Math.random() * 15; // Moderate jargon (design terms)
            sentenceLengthBase = 12 + Math.random() * 4; // 12-16 words
            break;
          case 'marketing':
            readabilityBase = 90 + Math.random() * 8; // Very readable (audience-focused)
            coherenceBase = 85 + Math.random() * 12; // High coherence (persuasive)
            concisionBase = 85 + Math.random() * 10; // Very concise (punchy)
            jargonBase = 10 + Math.random() * 10; // Low jargon (accessible)
            sentenceLengthBase = 11 + Math.random() * 3; // 11-14 words
            break;
          default:
            readabilityBase = 75 + Math.random() * 20;
            coherenceBase = 70 + Math.random() * 25;
            concisionBase = 65 + Math.random() * 30;
            jargonBase = 20 + Math.random() * 20;
            sentenceLengthBase = 14 + Math.random() * 6;
        }
        
        // Calculate overall clarity score (0-1 scale, higher is better)
        const clarityScore = (
          (readabilityBase * 0.3) +
          (coherenceBase * 0.3) +
          (concisionBase * 0.2) +
          ((100 - jargonBase) * 0.1) + // Less jargon = better clarity
          ((20 - Math.min(20, sentenceLengthBase)) * 0.1 * 5) // Shorter sentences = better clarity
        ) / 100;
        
        // Generate clarity insights for this channel
        const insights = [];
        if (readabilityBase < 70) insights.push("Consider simpler language");
        if (coherenceBase < 70) insights.push("Improve logical flow");
        if (concisionBase < 70) insights.push("Be more concise");
        if (jargonBase > 30) insights.push("Reduce technical jargon");
        if (sentenceLengthBase > 18) insights.push("Use shorter sentences");
        
        if (insights.length === 0) {
          insights.push("Communication is clear and effective");
        }
        
        return {
          id: channel.id,
          channel: channel.name,
          is_private: channel.is_private, // Add is_private property for lock icon
          score: Math.round(clarityScore * 100), // Convert to percentage (0-100%)
          readabilityScore: Math.round(readabilityBase),
          coherenceScore: Math.round(coherenceBase),
          concisionScore: Math.round(concisionBase),
          jargonLevel: Math.round(jargonBase),
          avgSentenceLength: Math.round(sentenceLengthBase),
          totalMessages: Math.floor(Math.random() * 100) + 20, // 20-120 messages
          clarityTrend: Math.random() > 0.5 ? 'improving' : 'declining',
          trendValue: Math.floor(Math.random() * 10) + 1, // 1-10% change
          insights: insights.slice(0, 2), // Top 2 insights
          topIssues: insights.length > 2 ? insights.slice(2) : [],
          complexity: Math.round(jargonBase), // Add complexity for table
          questionFrequency: Math.floor(Math.random() * 30) + 5, // Add question frequency
          actionItemFrequency: Math.floor(Math.random() * 25) + 3 // Add action item frequency
        };
      });
      
      return channelClarityData;
    } catch (error) {
      console.error("Error generating mock channel clarity data:", error);
      return [];
    }
  }
  
  try {
    const token = localStorage.getItem("token");
    const apiUrl = getApiBaseUrl();
    const orgId = organization._id || organization.id;
    
    const startDate = new Date(dateRange.startDate).toISOString();
    const endDate = new Date(dateRange.endDate).toISOString();
    
    // Create cache key with relevant filter parameters
    const cacheKey = `clarity_channels_${orgId}_${startDate}_${endDate}`;
    
    // Check cache first
    const cachedData = simpleCache.get(cacheKey);
    if (cachedData) {
      console.log('Using cached channel clarity data');
      return cachedData;
    }
    
    const response = await axios.get(
      `${apiUrl}/analytics/clarity/channels/${orgId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          startDate,
          endDate
        }
      }
    );
    
    const channelData = Array.isArray(response.data) ? response.data : [];
    
    // Get available channels to merge is_private property
    try {
      const availableChannels = await fetchSlackChannels(organization);
      const channelMap = new Map(availableChannels.map(ch => [ch.name, ch.is_private]));
      
      // Add is_private property to each channel data item
      const enrichedChannelData = channelData.map(item => ({
        ...item,
        is_private: channelMap.get(item.channel) || false
      }));
      
      // Cache the enriched response
      simpleCache.set(cacheKey, enrichedChannelData);
      
      return enrichedChannelData;
    } catch (error) {
      console.warn("Could not fetch channels for is_private property:", error);
      // Cache the response without is_private
      simpleCache.set(cacheKey, channelData);
      return channelData;
    }
  } catch (error) {
    console.error("Error generating channel clarity data:", error);
    return [];
  }
}; 



// Generate topic cluster trends data
export const generateTopicTrendsData = async (organization, channelIds, dateRange) => {
  if (!organization) return [];
  
  // Return mock topic cluster trends data when using mock data in development
  if (shouldBypassSlackConnection()) {
    console.log('Using mock data - generating topic cluster trends data');
    
    // Define the 5 topic clusters (same as TopicCloudChart)
    const clusters = [
      'productDevelopment',     // Product & Development
      'customerSupport',        // Customer & Support  
      'operationsProcess',      // Operations & Process
      'businessStrategy',       // Business & Strategy
      'communicationCulture'    // Communication & Culture
    ];
    
    // Generate time series data for the last 7 days
    const trendsData = [];
    const endDate = new Date(dateRange.endDate);
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(endDate);
      date.setDate(date.getDate() - i);
      
      const dataPoint = {
        date: date.toISOString().split('T')[0]
      };
      
      // Add realistic cluster values with exact mention counts
      clusters.forEach((cluster, index) => {
        // Create different realistic patterns for each cluster
        let baseValue;
        let variance;
        
        switch (cluster) {
          case 'productDevelopment':
            baseValue = Math.floor(Math.random() * 4) + 1; // 1-4 mentions typically
            variance = Math.floor(Math.random() * 3); // 0-2 additional
            break;
          case 'customerSupport':
            baseValue = Math.floor(Math.random() * 3); // 0-2 mentions
            variance = Math.floor(Math.random() * 2); // 0-1 additional
            break;
          case 'operationsProcess':
            baseValue = Math.floor(Math.random() * 5) + 1; // 1-5 mentions (often highest)
            variance = Math.floor(Math.random() * 2); // 0-1 additional
            break;
          case 'businessStrategy':
            baseValue = Math.floor(Math.random() * 2); // 0-1 mentions (usually lowest)
            variance = Math.floor(Math.random() * 2); // 0-1 additional
            break;
          case 'communicationCulture':
            baseValue = Math.floor(Math.random() * 4) + 1; // 1-4 mentions
            variance = Math.floor(Math.random() * 3); // 0-2 additional
            break;
          default:
            baseValue = 1;
            variance = 0;
        }
        
        // Calculate final value - realistic exact counts
        const value = baseValue + variance;
        dataPoint[cluster] = value;
      });
      
      trendsData.push(dataPoint);
    }
    
    return trendsData;
  }
  
  try {
    const token = localStorage.getItem("token");
    const apiUrl = getApiBaseUrl();
    const orgId = organization._id || organization.id;
    
    const startDate = new Date(dateRange.startDate).toISOString();
    const endDate = new Date(dateRange.endDate).toISOString();
    
    // Create cache key with relevant filter parameters
    const cacheKey = `topic_trends_${orgId}_${startDate}_${endDate}_${channelIds ? channelIds.join('-') : 'all'}`;
    
    // Check cache first
    const cachedData = simpleCache.get(cacheKey);
    if (cachedData) {
      console.log('Using cached topic trends data');
      return cachedData;
    }
    
    const response = await axios.get(
      `${apiUrl}/analytics/topics/cluster-trends/${orgId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          startDate,
          endDate,
          channels: channelIds ? channelIds.join(',') : undefined
        }
      }
    );
    
    const trendsData = Array.isArray(response.data) ? response.data : [];
    
    // Cache the response
    simpleCache.set(cacheKey, trendsData);
    
    return trendsData;
  } catch (error) {
    console.error("Error generating topic cluster trends data:", error);
    return [];
  }
};

// Generate channel topics data
export const generateChannelTopicsData = async (organization, dateRange) => {
  return [];
};

// Generate network analysis data
export const generateNetworkData = async (organization, channelIds, dateRange) => {
  if (!organization) return { nodes: [], links: [] };
  
  const cacheKey = `network_data_${organization._id || organization.id}_${JSON.stringify(channelIds)}_${dateRange.startDate.toISOString()}_${dateRange.endDate.toISOString()}`;
  
  // Check cache first
  const cachedData = simpleCache.get(cacheKey);
  if (cachedData) {
    console.log('Using cached network data');
    return cachedData;
  }
  
  try {
    console.log('Fetching network data from API...');
    
    // Fetch network report data from the API
    const reportData = await fetchNetworkReportData(organization, dateRange, channelIds);
    
    if (!reportData || !reportData.network) {
      console.log('No network data available from API');
      return { nodes: [], links: [] };
    }
    
    // Extract the most recent day's network graph data
    const networkGraphData = reportData.network.networkGraph;
    const availableDates = Object.keys(networkGraphData || {}).sort().reverse();
    
    if (availableDates.length === 0) {
      console.log('No network graph data available');
      return { nodes: [], links: [] };
    }
    
    // Use the most recent date's data, or aggregate if multiple dates
    let result = { nodes: [], links: [] };
    
    if (availableDates.length === 1) {
      // Single date - use directly
      result = networkGraphData[availableDates[0]];
      } else {
      // Multiple dates - aggregate the data
      result = aggregateNetworkData(networkGraphData, availableDates);
    }
    
    // Filter by channels if specified
    if (channelIds && channelIds.length > 0) {
      console.log('Applying channel filtering to network data on frontend...');
      
      // For now, we'll keep the full network data since true channel filtering
      // would require recalculating the network from channel-specific message data
      // This is a limitation of the current approach - network analysis works best
      // with the full organization data
      console.log('Note: Network analysis shows organization-wide connections.');
      console.log('Channel filtering for network data would require backend recalculation.');
    }
    
    // Cache the result
    simpleCache.set(cacheKey, result, 30 * 60 * 1000); // Cache for 30 minutes
    
    console.log(`Network data loaded: ${result.nodes.length} nodes, ${result.links.length} links`);
    return result;
    
  } catch (error) {
    console.error("Error fetching network data from API:", error);
    
    // Fallback to empty data
    return { nodes: [], links: [] };
  }
};
    
// Helper function to aggregate network data across multiple dates
function aggregateNetworkData(networkGraphData, dates) {
  const nodeMap = new Map();
  const linkMap = new Map();
  const nodeMetrics = new Map(); // To collect all values for proper averaging
      
  // First pass: collect all values for each node across all dates
  dates.forEach(date => {
    const dayData = networkGraphData[date];
    if (!dayData || !dayData.nodes) return;
    
    dayData.nodes.forEach(node => {
      if (!nodeMetrics.has(node.id)) {
        nodeMetrics.set(node.id, {
          nodeData: { ...node }, // Base node data
          values: [],
          publicIndexValues: [],
          degreeCentralityValues: [],
          betweennessCentralityValues: [],
          closenessCentralityValues: [],
          eigenvectorCentralityValues: [],
          publicMessagesSum: 0,
          privateMessagesSum: 0,
          hasMessageCounts: false,
        });
      }
      
      const metrics = nodeMetrics.get(node.id);
      
      // Collect all values across dates
      metrics.values.push(node.value || 0);
      metrics.publicIndexValues.push(typeof node.publicIndex === 'number' ? node.publicIndex : 0);
      metrics.degreeCentralityValues.push(node.degreeCentrality || 0);
      metrics.betweennessCentralityValues.push(node.betweennessCentrality || 0);
      metrics.closenessCentralityValues.push(node.closenessCentrality || 0);
      metrics.eigenvectorCentralityValues.push(node.eigenvectorCentrality || 0);

      const ca = node.channelActivity;
      if (ca && (ca.publicMessages != null || ca.privateMessages != null)) {
        metrics.hasMessageCounts = true;
        metrics.publicMessagesSum += Number(ca.publicMessages ?? 0);
        metrics.privateMessagesSum += Number(ca.privateMessages ?? 0);
      }
      
      // Update other properties (title, name, etc.) from the most recent data
      if (node.title) metrics.nodeData.title = node.title;
      if (node.channelActivity) metrics.nodeData.channelActivity = node.channelActivity;
    });
  });
  
  // Second pass: calculate proper averages
  nodeMetrics.forEach((metrics, nodeId) => {
    const nodeData = { ...metrics.nodeData };
    
    // Calculate proper averages
    const avgValue = metrics.values.reduce((sum, val) => sum + val, 0) / metrics.values.length;
    const avgPublicIndex = metrics.publicIndexValues.reduce((sum, val) => sum + val, 0) / metrics.publicIndexValues.length;
    const avgDegree = metrics.degreeCentralityValues.reduce((sum, val) => sum + val, 0) / metrics.degreeCentralityValues.length;
    const avgBetweenness = metrics.betweennessCentralityValues.reduce((sum, val) => sum + val, 0) / metrics.betweennessCentralityValues.length;
    const avgCloseness = metrics.closenessCentralityValues.reduce((sum, val) => sum + val, 0) / metrics.closenessCentralityValues.length;
    const avgEigenvector = metrics.eigenvectorCentralityValues.reduce((sum, val) => sum + val, 0) / metrics.eigenvectorCentralityValues.length;
    
    // Update the node with averaged values
    nodeData.value = Math.round(avgValue * 10) / 10;
    nodeData.publicIndex = Math.round(avgPublicIndex * 1000) / 1000;
    nodeData.degreeCentrality = Math.round(avgDegree * 1000) / 1000;
    nodeData.betweennessCentrality = Math.round(avgBetweenness * 1000) / 1000;
    nodeData.closenessCentrality = Math.round(avgCloseness * 1000) / 1000;
    nodeData.eigenvectorCentrality = Math.round(avgEigenvector * 1000) / 1000;

    if (nodeData.channelActivity && metrics.hasMessageCounts) {
      nodeData.channelActivity = {
        ...nodeData.channelActivity,
        publicMessages: metrics.publicMessagesSum,
        privateMessages: metrics.privateMessagesSum,
      };
    }
    
    console.log(`Node ${nodeData.name}: publicIndex averaged from [${metrics.publicIndexValues.join(', ')}] = ${avgPublicIndex} (${Math.round(avgPublicIndex * 100)}% private)`);
    
    nodeMap.set(nodeId, nodeData);
  });
      
  // Aggregate links across all dates
  dates.forEach(date => {
    const dayData = networkGraphData[date];
    if (!dayData || !dayData.links) return;
    
    dayData.links.forEach(link => {
      const linkKey = `${link.source}-${link.target}`;
      const reverseLinkKey = `${link.target}-${link.source}`;
      
      if (!linkMap.has(linkKey) && !linkMap.has(reverseLinkKey)) {
        linkMap.set(linkKey, { ...link });
      } else {
        // Update link strength by summing
        const existingLink = linkMap.get(linkKey) || linkMap.get(reverseLinkKey);
        existingLink.value += link.value;
      }
    });
  });
  
  return {
    nodes: Array.from(nodeMap.values()),
    links: Array.from(linkMap.values())
  };
        }
        
// New function to fetch network report data
async function fetchNetworkReportData(organization, dateRange, channelIds = []) {
  try {
    const token = localStorage.getItem("token");
    const apiUrl = getApiBaseUrl();
    const orgId = organization._id || organization.id;
    
    // Convert date range to API format
    const startDate = dateRange.startDate.toISOString().split('T')[0];
    const endDate = dateRange.endDate.toISOString().split('T')[0];
    
    // Build URL without channel filters (network data needs to be filtered on frontend)
    // We don't pass channels parameter because backend channel filtering for network data
    // doesn't work properly - it just returns the full network data anyway
    let url = `${apiUrl}/slack-reports/organization/${orgId}?reportType=network&startDate=${startDate}&endDate=${endDate}`;
    
    console.log(`Fetching network data from: ${url}`);
    if (channelIds && channelIds.length > 0) {
      console.log(`Note: Channel filtering will be applied on frontend for channels: ${channelIds.join(', ')}`);
    }

    try {
      const response = await axios.get(url,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      return response.data.data?.reportData;
    } catch (error) {
      // If we get a 404, it means no data exists yet - trigger a sync
      if (error.response?.status === 404) {
        console.log('No network data found. Triggering sync to generate data...');
        
        // Trigger sync with the requested date range
        await axios.post(
          `${apiUrl}/slack-reports/organization/${orgId}/sync`,
          {
            dateRange: {
              startDate: startDate,
              endDate: endDate,
              range: 'custom'
            }
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
    
        console.log('Sync triggered! Data will be available shortly. Please refresh the page in a few moments.');
        
        // Return null to indicate data is being generated
        return null;
      }
      
      throw error;
    }
  } catch (error) {
    console.error("Error fetching network report data:", error);
    throw error;
  }
}

// Calculate network metrics from API data
export const calculateNetworkMetrics = async (organization, channelIds, dateRange) => {
  if (!organization) {
    return {
      networkDensity: 0,
      centralityScores: [],
      siloIndex: 0,
      networkCentralization: 0,
      avgClusteringCoefficient: 0
    };
  }
  
  try {
    console.log('Fetching network metrics from API...');
    
    // Fetch network report data from the API
    const reportData = await fetchNetworkReportData(organization, dateRange, channelIds);
    
    if (!reportData || !reportData.network) {
      console.log('No network metrics available from API');
    return {
      networkDensity: 0,
      centralityScores: [],
        siloIndex: 0,
        networkCentralization: 0,
        avgClusteringCoefficient: 0
    };
  }
  
    // Extract network metrics and centrality scores
    const networkMetrics = reportData.network.networkMetrics;
    const centralityScores = reportData.network.centralityScores;
    
    // Get the most recent data
    const availableDates = Object.keys(networkMetrics || {}).sort().reverse();
    
    if (availableDates.length === 0) {
      console.log('No network metrics data available');
      return {
        networkDensity: 0,
        centralityScores: [],
        siloIndex: 0,
        networkCentralization: 0,
        avgClusteringCoefficient: 0
      };
    }
    
    // Use the most recent date's metrics, or average if multiple dates
    let metrics = {
      networkDensity: 0,
      siloIndex: 0,
      networkCentralization: 0,
      avgClusteringCoefficient: 0,
      bottleneckIndex: 0
    };
    
    let aggregatedCentralityScores = [];
    
    if (availableDates.length === 1) {
      // Single date - use directly
      metrics = networkMetrics[availableDates[0]];
      aggregatedCentralityScores = centralityScores[availableDates[0]] || [];
    } else {
      // Multiple dates - average the metrics
      let totalDensity = 0;
      let totalSilo = 0;
      let totalCentralization = 0;
      let totalClustering = 0;
      let totalBottleneck = 0;
      
      availableDates.forEach(date => {
        const dayMetrics = networkMetrics[date];
        if (dayMetrics) {
          totalDensity += parseFloat(dayMetrics.networkDensity) || 0;
          totalSilo += parseFloat(dayMetrics.siloIndex) || 0;
          totalCentralization += parseFloat(dayMetrics.networkCentralization) || 0;
          totalClustering += parseFloat(dayMetrics.avgClusteringCoefficient) || 0;
          totalBottleneck += parseFloat(dayMetrics.bottleneckIndex) || 0;
        }
      });
    
      const count = availableDates.length;
      metrics = {
        networkDensity: (totalDensity / count).toFixed(1),
        siloIndex: (totalSilo / count).toFixed(1),
        networkCentralization: (totalCentralization / count).toFixed(1),
        avgClusteringCoefficient: (totalClustering / count).toFixed(1),
        bottleneckIndex: (totalBottleneck / count).toFixed(1)
      };
      
      // Aggregate centrality scores across dates
      const userMetrics = new Map();
      availableDates.forEach(date => {
        const dayScores = centralityScores[date] || [];
        dayScores.forEach(score => {
          if (!userMetrics.has(score.userId)) {
            userMetrics.set(score.userId, {
              name: score.name,
              title: score.title || '',
              degreeCentralityValues: [],
              betweennessCentralityValues: [],
              closenessCentralityValues: [],
              eigenvectorCentralityValues: [],
              connectionCountValues: []
            });
          }
          const metrics = userMetrics.get(score.userId);
          metrics.degreeCentralityValues.push(score.degreeCentrality || 0);
          metrics.betweennessCentralityValues.push(score.betweennessCentrality || 0);
          metrics.closenessCentralityValues.push(score.closenessCentrality || 0);
          metrics.eigenvectorCentralityValues.push(score.eigenvectorCentrality || 0);
          metrics.connectionCountValues.push(score.connectionCount || 0);
        });
      });
      
      aggregatedCentralityScores = [];
      userMetrics.forEach((um, userId) => {
        const avgDegree = um.degreeCentralityValues.reduce((sum, v) => sum + v, 0) / um.degreeCentralityValues.length;
        const avgBetweenness = um.betweennessCentralityValues.reduce((sum, v) => sum + v, 0) / um.betweennessCentralityValues.length;
        const avgCloseness = um.closenessCentralityValues.reduce((sum, v) => sum + v, 0) / um.closenessCentralityValues.length;
        const avgEigenvector = um.eigenvectorCentralityValues.reduce((sum, v) => sum + v, 0) / um.eigenvectorCentralityValues.length;
        const avgConnectionCount = um.connectionCountValues.reduce((sum, v) => sum + v, 0) / um.connectionCountValues.length;
        aggregatedCentralityScores.push({
          id: userId,
          name: um.name,
          title: um.title,
          degreeCentrality: Math.round(avgDegree * 1000) / 1000,
          betweennessCentrality: Math.round(avgBetweenness * 1000) / 1000,
          closenessCentrality: Math.round(avgCloseness * 1000) / 1000,
          eigenvectorCentrality: Math.round(avgEigenvector * 1000) / 1000,
          connectionCount: Math.round(avgConnectionCount)
        });
      });
    }
    
    // Format centrality scores for the UI
    const formattedCentralityScores = aggregatedCentralityScores
      .slice(0, 10) // Top 10 to have enough data for sorting
      .map(score => ({
        id: score.id,
        name: score.name,
        title: score.title || '',
        score: score.connectionCount,
        degreeCentrality: score.degreeCentrality || 0,
        betweennessCentrality: score.betweennessCentrality || 0,
        closenessCentrality: score.closenessCentrality || 0,
        eigenvectorCentrality: score.eigenvectorCentrality || 0,
        connectionCount: score.connectionCount || 0
      }));
    
    console.log(`Network metrics loaded: density=${metrics.networkDensity}%, silo=${metrics.siloIndex}%`);
  
  return {
      networkDensity: parseFloat(metrics.networkDensity),
      centralityScores: formattedCentralityScores,
      siloIndex: parseFloat(metrics.siloIndex),
      networkCentralization: parseFloat(metrics.networkCentralization),
      avgClusteringCoefficient: parseFloat(metrics.avgClusteringCoefficient),
      bottleneckIndex: parseFloat(metrics.bottleneckIndex)
    };
    
  } catch (error) {
    console.error("Error fetching network metrics from API:", error);
    
    // Fallback to empty metrics
    return {
      networkDensity: 0,
      centralityScores: [],
      siloIndex: 0,
      networkCentralization: 0,
      avgClusteringCoefficient: 0,
      bottleneckIndex: 0
  };
  }
};

// NEW FUNCTION: Get real time series data from API
export const getNetworkTimeSeriesData = async (organization, channelIds, dateRange) => {
  if (!organization) {
    return {
      networkDensityData: [],
      siloIndexData: [],
      networkCentralizationData: [],
      bottleneckIndexData: []
    };
  }
  
  try {
    console.log('Fetching network time series data from API...');
    
    // Fetch network report data from the API
    const reportData = await fetchNetworkReportData(organization, dateRange, channelIds);
    
    if (!reportData || !reportData.network || !reportData.network.networkMetrics) {
      console.log('No network time series data available from API');
      return {
        networkDensityData: [],
        siloIndexData: [],
        networkCentralizationData: [],
        bottleneckIndexData: []
      };
    }
    
    // Extract network metrics
    const networkMetrics = reportData.network.networkMetrics;
    
    console.log('Raw network metrics keys:', Object.keys(networkMetrics));
    
    // Convert the date-keyed object to arrays for charts
    const networkDensityData = [];
    const siloIndexData = [];
    const networkCentralizationData = [];
    const bottleneckIndexData = [];
    
    // Sort dates to ensure correct chronological order
    const sortedDates = Object.keys(networkMetrics).sort((a, b) => {
      try {
        // Parse the date strings like "May 16 2025, Friday" to compare them properly
        // FIXED: Use timezone-safe parsing for sorting as well
        const parseDate = (dateStr) => {
          const dateWithoutDay = dateStr.split(',')[0].trim();
          const parts = dateWithoutDay.split(' ');
          
          if (parts.length === 3) {
            const [monthName, day, year] = parts;
            
            // Convert month name to number
            const monthMap = {
              'Jan': 0, 'January': 0, 'Feb': 1, 'February': 1, 'Mar': 2, 'March': 2,
              'Apr': 3, 'April': 3, 'May': 4, 'Jun': 5, 'June': 5, 'Jul': 6, 'July': 6,
              'Aug': 7, 'August': 7, 'Sep': 8, 'September': 8, 'Oct': 9, 'October': 9,
              'Nov': 10, 'November': 10, 'Dec': 11, 'December': 11
            };
            
            const monthNum = monthMap[monthName];
            if (monthNum !== undefined) {
              // Create a Date object for comparison (but don't convert to ISO)
              return new Date(parseInt(year), monthNum, parseInt(day));
            }
          }
          
          // Fallback to regular Date parsing
          return new Date(dateWithoutDay);
        };
        
        const dateA = parseDate(a);
        const dateB = parseDate(b);
        
        // If parsing fails, fall back to string comparison
        if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
          return a.localeCompare(b);
        }
        
        return dateA - dateB;
      } catch (error) {
        console.error('Error sorting dates:', error);
        // Fallback to string comparison
        return a.localeCompare(b);
      }
    });
    
    sortedDates.forEach(dateKey => {
      const dayMetrics = networkMetrics[dateKey];
      if (dayMetrics) {
        // Parse date key format like "May 16 2025, Friday" to ISO format for charts
        // FIXED: Avoid timezone conversion that was causing off-by-one day issues
        let isoDate;
        try {
          // Remove the day name (", Friday") and parse the date
          const dateWithoutDay = dateKey.split(',')[0].trim();
          console.log(`Parsing date: "${dateKey}" -> "${dateWithoutDay}"`);
          
          // Parse "May 16 2025" format manually to avoid timezone issues
          const parts = dateWithoutDay.split(' ');
          if (parts.length === 3) {
            const [monthName, day, year] = parts;
            
            // Convert month name to number (0-indexed)
            const monthMap = {
              'Jan': 0, 'January': 0,
              'Feb': 1, 'February': 1,
              'Mar': 2, 'March': 2,
              'Apr': 3, 'April': 3,
              'May': 4, 'May': 4,
              'Jun': 5, 'June': 5,
              'Jul': 6, 'July': 6,
              'Aug': 7, 'August': 7,
              'Sep': 8, 'September': 8,
              'Oct': 9, 'October': 9,
              'Nov': 10, 'November': 10,
              'Dec': 11, 'December': 11
            };
            
            const monthNum = monthMap[monthName];
            if (monthNum !== undefined) {
              // Create date in local timezone and format directly as YYYY-MM-DD
              const dayNum = parseInt(day);
              const yearNum = parseInt(year);
              
              // Format as ISO date string manually to avoid timezone conversion
              const paddedMonth = String(monthNum + 1).padStart(2, '0');
              const paddedDay = String(dayNum).padStart(2, '0');
              isoDate = `${yearNum}-${paddedMonth}-${paddedDay}`;
              
              console.log(`Converted "${dateKey}" to ISO date: "${isoDate}"`);
            } else {
              console.warn(`Unknown month: ${monthName} in date: ${dateKey}`);
              // Fallback to Date parsing but still avoid timezone conversion
              const parsedDate = new Date(dateWithoutDay);
              if (!isNaN(parsedDate.getTime())) {
                const year = parsedDate.getFullYear();
                const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
                const day = String(parsedDate.getDate()).padStart(2, '0');
                isoDate = `${year}-${month}-${day}`;
              } else {
                isoDate = new Date().toISOString().split('T')[0];
              }
            }
          } else {
            console.warn(`Unexpected date format: ${dateWithoutDay}`);
            // Fallback to Date parsing but still avoid timezone conversion
            const parsedDate = new Date(dateWithoutDay);
            if (!isNaN(parsedDate.getTime())) {
              const year = parsedDate.getFullYear();
              const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
              const day = String(parsedDate.getDate()).padStart(2, '0');
              isoDate = `${year}-${month}-${day}`;
            } else {
              isoDate = new Date().toISOString().split('T')[0];
            }
          }
        } catch (error) {
          console.error(`Error parsing date ${dateKey}:`, error);
          // Fallback to current date
          isoDate = new Date().toISOString().split('T')[0];
        }
        
        networkDensityData.push({
          date: isoDate,
          value: parseFloat(dayMetrics.networkDensity) || 0
        });
        
        siloIndexData.push({
          date: isoDate,
          value: parseFloat(dayMetrics.siloIndex) || 0
        });
        
        networkCentralizationData.push({
          date: isoDate,
          value: parseFloat(dayMetrics.networkCentralization) || 0
        });
        
        bottleneckIndexData.push({
          date: isoDate,
          value: parseFloat(dayMetrics.bottleneckIndex) || 0
        });
      }
    });
    
    console.log(`Network time series loaded: ${sortedDates.length} data points`);
    console.log('Original database date keys:', sortedDates);
    console.log('Final density data with converted dates:', networkDensityData);
    console.log('Final silo data with converted dates:', siloIndexData);
    
    return {
      networkDensityData,
      siloIndexData,
      networkCentralizationData,
      bottleneckIndexData
    };
    
  } catch (error) {
    console.error("Error fetching network time series data from API:", error);
    
    // Fallback to empty data
    return {
      networkDensityData: [],
      siloIndexData: [],
      networkCentralizationData: [],
      bottleneckIndexData: []
    };
  }
};